import { t } from "i18next";
import { TFunction } from "i18next";
import i18n from "../i18n/i18n";

import {
  VaporIcon,
  Badge,
  IconButton,
  TextField,
  Box,
  Button,
  Typography,
} from "@vapor/v3-components";
import {
  faTableList,
  faTrashCan,
  faDownload,
  faEye,
} from "@fortawesome/pro-regular-svg-icons";
import { getFileExtension, returnIconExtension, isVideo } from "../utils/util";
import {
  GridColDef,
  GridRenderEditCellParams,
  GridRenderCellParams,
  GetApplyQuickFilterFn,
} from "@mui/x-data-grid-pro";
import { getFullLocale, convertColumnType } from "../utils/util";
import { ColumnDescriptor } from "../services/Models/FilterDescriptorModel";
import { ColumnType } from "../services/enums";
import { TextFieldGrid } from "./TextFieldGrid";

// Interfaccia per i parametri del formato Integer
interface IntegerFormatParams {
  useThousandsSeparator: boolean;
  locale: string;
}

// Interfaccia per i parametri del formato DateTime
interface DateTimeFormatParams {
  pattern: string;
}

// Tipo per il formato booleano
type BooleanFormatType = "x" | "T" | "Y" | "1";

// Interfaccia per i parametri del formato booleano
interface BooleanFormatParams {
  type: BooleanFormatType;
}

// Interfaccia per i parametri del formato decimale
interface DecimalFormatParams {
  decimals: number | "*";
  locale: string;
  useThousandsSeparator: boolean;
}

export interface IStandardColumnOption {
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  filterable?: boolean;
  sortable?: boolean;
  visible?: boolean; // Require management of columnVisibilityModel like for object views
  editable?: boolean;
  onClickIcon?: (idRow: number, type?: string, row?: any) => void;
}

const currentLocale = i18n.language;

export const StandardColumns = {
  //#region ===== Common =====

  /** field: "id" */
  id: (option?: IStandardColumnOption) => {
    const col: GridColDef = {
      field: "id",
      headerName: `${t("ID")}`,
      type: "number",
      width: 50,
      editable: false,
      hideable: true,
      filterable: false,
      sortable: false,
      visible: false,
      ...option,
    };
    return col;
  },
  idObject: (option?: IStandardColumnOption) => {
    const col: GridColDef = {
      field: "idObject",
      headerName: `${t("ID-Object")}`,
      type: "number",
      width: 50,
      editable: false,
      hideable: true,
      filterable: false,
      sortable: false,
      visible: false,
      ...option,
    };
    return col;
  },
  idDoc: (option?: IStandardColumnOption) => {
    const col: GridColDef = {
      field: "idDoc",
      headerName: `${t("ID-Doc")}`,
      type: "number",
      width: 50,
      editable: false,
      hideable: true,
      filterable: false,
      sortable: false,
      visible: false,
      ...option,
    };
    return col;
  },
  MD5: (option?: IStandardColumnOption) => {
    const col: GridColDef = {
      field: "MD5",
      headerName: "MD5",
      type: "string",
      width: 50,
      editable: false,
      hideable: true,
      filterable: false,
      sortable: false,
      visible: false,
      ...option,
    };
    return col;
  },
  file: (option?: IStandardColumnOption) => {
    const col: GridColDef = {
      field: "file",
      headerName: t("file"),
      width: 50,
      filterable: false,
      hideable: false,
      pinnable: false,
      resizable: false,
      sortable: false,
      type: "number",
      align: "center",
      renderCell: (params) => {
        let [numFile, ...extension] = (params.value || "0,").split(","); // Divide al primo `,`
        let value = Number(numFile);
        if (value === 0) extension = "";
        else if (value > 1) extension = null;
        else value = 0;
        const rowId = params.row.id; // Ottieni l'id della riga

        // Funzione di click
        const showDoc = () => {
          if (option && option.onClickIcon) option.onClickIcon(rowId);
        };
        return (
          <IconButton onClick={(e) => showDoc()} edge="end" sx={{ padding: 0 }}>
            <Badge
              badgeContent={value === 9999 ? 0 : value}
              max={9999}
              size="small"
              variant="standard"
              //          color="info"
              color="counter"
            >
              <VaporIcon
                icon={returnIconExtension(extension)}
                size="xl"
                color="primary"
              />
            </Badge>
          </IconButton>
        );
      },
    };
    return col;
  },
  action: (option?: IStandardColumnOption) => {
    const col: GridColDef = {
      field: "action",
      headerName: t("actions"),
      width: 60,
      filterable: false,
      hideable: false,
      pinnable: false,
      resizable: false,
      sortable: false,
      type: "boolean",
      align: "center",
      renderCell: (params) => {
        const value = params.row.action; //params.value;
        if (value) {
          const rowId = params.row.id; // Ottieni l'id della riga

          // Funzione di click
          const showActions = () => {
            if (option && option.onClickIcon) option.onClickIcon(rowId);
          };
          return (
            <IconButton
              onClick={(e) => showActions()}
              edge="end"
              sx={{ padding: 0 }}
            >
              <VaporIcon icon={faTableList} size="xl" color="primary" />
            </IconButton>
          );
        } else {
          return <></>;
        }
      },
    };
    return col;
  },
  fileName: (option?: IStandardColumnOption, noDownloadVideo?: boolean) => {
    const col: GridColDef = {
      field: "fileName",
      headerName: `${t("file-name")}`,
      type: "string",
      width: 300,
      editable: false,
      hideable: true,
      filterable: true,
      sortable: true,
      visible: true,
      renderCell: (params) => {
        // Funzione di click
        const value = params.value;
        let download: boolean = true;
        if (noDownloadVideo && isVideo(getFileExtension(value)))
          download = false;
        const extension = getFileExtension(value);
        const row = params.row;
        if (value) {
          const rowId = params.row.id; // Ottieni l'id della riga

          // Funzione di click
          const showActions = (type: string) => {
            if (option && option.onClickIcon)
              option.onClickIcon(rowId, type, row);
          };
          // const handleSelectFolder = (
          //   event: React.ChangeEvent<HTMLInputElement>
          // ) => {
          //   if (option && option.onClickIcon) {
          //     const files = event.target.files;
          //     if (files && files.length > 0) {
          //       // Ricava il nome della directory dal primo file selezionato
          //       const directoryPath = files[0].webkitRelativePath.split("/")[0];
          //       option.onClickIcon(rowId, "show", directoryPath);
          //     }
          //   }
          // };
          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  mr: 2,
                  spacing: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {download &&
                row.tipoDocumento !== "YOUTUBE" &&
                row.tipoDocumento !== "LINK" ? (
                  <IconButton
                    onClick={(e) => showActions("download")}
                    edge="end"
                    sx={{ padding: 0, marginRight: 0.3 }}
                  >
                    <VaporIcon icon={faDownload} size="l" color="primary" />
                  </IconButton>
                ) : (
                  <Box
                    sx={{ width: "40px", height: "40px", marginRight: 0.3 }}
                  />
                )}
                <IconButton
                  onClick={(e) => showActions("preview")}
                  edge="end"
                  sx={{ padding: 0 }}
                >
                  <VaporIcon icon={faEye} size="l" color="primary" />
                </IconButton>
              </Box>
              <Typography
                color="contentInformative"
                component="div"
                variant="bodyLargeHeavy"
                onClick={(e) => showActions("show")}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {params.value}
              </Typography>
            </div>
          );
        } else {
          return <></>;
        }
      },

      ...option,
    };
    return col;
  },
  descrizione: (option?: IStandardColumnOption) => {
    const col: GridColDef = {
      field: "descrizione",
      headerName: `${t("Descrizione")}`,
      type: "string",
      width: 200,
      editable: false,
      hideable: true,
      filterable: true,
      sortable: true,
      visible: true,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row._actions_) return undefined;
        else
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: "100%",
                borderBottom: "2px solid #0076ad",
              }}
            >
              {params.value}
            </div>
          );
      },

      renderEditCell: (params: GridRenderEditCellParams) => {
        if (params.row._actions_) return <TextFieldGrid cellParam={params} />;
        else return undefined;
      },
      ...option,
    };
    return col;
  },
  tipoDocumento: (option?: IStandardColumnOption) => {
    const col: GridColDef = {
      field: "tipoDocumento",
      headerName: `${t("doc-type")}`,
      type: "string",
      width: 200,
      editable: false,
      hideable: true,
      filterable: true,
      sortable: true,
      visible: true,
      align: "center",
      renderCell: (params) => {
        // Funzione di click
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ mr: 2 }}>
              <VaporIcon
                icon={returnIconExtension(params.value)}
                size="xl"
                color="primary"
              />
            </Box>
            {params.value}
          </div>
        );
      },

      ...option,
    };
    return col;
  },
  dimensione: (option?: IStandardColumnOption) => {
    const col: GridColDef = {
      field: "dimensione",
      headerName: `${t("file-size")}`,
      type: "number",
      width: 100,
      editable: false,
      hideable: true,
      filterable: true,
      sortable: true,
      visible: true,
      //   valueFormatter: (value) => {
      //     return numberFormatter(value);
      //   },
      ...option,
    };
    return col;
  },
  _actions_: (option?: IStandardColumnOption) => {
    const col: GridColDef = {
      field: "_actions_",
      headerName: "", //t("cancella"),
      type: "actions",
      width: 80,
      filterable: false,
      hideable: false,
      pinnable: false,
      resizable: false,
      sortable: false,
      align: "center",
      visible: true,
      renderCell: (params) => {
        const value = params.row._actions_; //params.value;
        if (value) {
          const rowId = params.row.id; // Ottieni l'id della riga
          const idDoc = params.row.idDoc; // Ottieni l'idDoc

          // Funzione di click
          const deleteDocuments = () => {
            if (option && option.onClickIcon) option.onClickIcon(idDoc);
          };
          return (
            <IconButton
              onClick={(e) => deleteDocuments()}
              edge="end"
              sx={{ padding: 0 }}
            >
              <VaporIcon icon={faTrashCan} size="m" color="error" />
            </IconButton>
          );
        } else {
          return <></>;
        }
      },
      ...option,
    };
    return col;
  },
};

// Number formatter per colonne ID
export const numberFormatter = (value: any) => {
  if (value === null || value === undefined || value === "") {
    return value;
  }

  // Converti in numero se è una stringa
  let numValue: number;
  if (typeof value === "string") {
    numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return value;
    }
  } else if (typeof value === "number") {
    numValue = value;
  } else {
    return value;
  }

  return numValue.toString();
};

// Funzione per parsare il formato Integer
export const parseIntegerFormat = (
  format: string
): IntegerFormatParams | null => {
  if (!format || format === "") {
    return null;
  }

  // Pattern 1: "int:" - nessun separatore
  if (format === "int:") {
    return {
      useThousandsSeparator: false,
      locale: "en",
    };
  }

  // Pattern 2: "dec:0|lang=XX,tsep=1" - con separatore migliaia
  const match = format.match(/^dec:0\|lang=([a-z]{2}),tsep=1$/i);

  if (match) {
    const [, locale] = match;
    return {
      useThousandsSeparator: true,
      locale: locale,
    };
  }

  return null;
};

// Formatter integer migliorato
export const integerFormatter = (
  value: any,
  format?: string,
  defaultLocale: string = "it"
) => {
  // Gestisci valori null/undefined/empty
  if (value === null || value === undefined || value === "") {
    return value;
  }

  // Converti il valore in numero se è una stringa
  let numValue: number;
  if (typeof value === "string") {
    numValue = parseInt(value, 10);
    // Se la conversione fallisce (NaN), restituisci il valore originale
    if (isNaN(numValue)) {
      return value;
    }
  } else if (typeof value === "number") {
    numValue = Math.floor(value); // Assicurati che sia un intero
  } else {
    return value;
  }

  // Parsa il formato
  const formatParams = parseIntegerFormat(format || "");

  // Se non c'è un formato valido, restituisci il numero come stringa senza separatori
  if (!formatParams) {
    return numValue.toString();
  }

  const { useThousandsSeparator, locale } = formatParams;

  // Se non usa separatori, restituisci come stringa
  if (!useThousandsSeparator) {
    return numValue.toString();
  }

  // Usa Intl.NumberFormat con separatori
  const formatter = new Intl.NumberFormat(locale, {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return formatter.format(numValue);
};

// dateFormatter per chiamate senza formato custom
export const dateFormatter = (value: any, locale: string = "it") => {
  // Usa il nuovo dateTimeFormatter senza formato (default)
  return dateTimeFormatter(value, undefined, locale);
};

// Funzione per parsare il formato DateTime
export const parseDateTimeFormat = (
  format: string
): DateTimeFormatParams | null => {
  if (!format || format === "") {
    return null;
  }

  // Pattern: "dt:..."
  const match = format.match(/^dt:(.+)$/);

  if (!match) {
    return null;
  }

  const [, pattern] = match;

  return {
    pattern: pattern,
  };
};

// Funzione helper per ottenere il nome del mese localizzato
const getMonthName = (date: Date, locale: string): string => {
  const formatter = new Intl.DateTimeFormat(locale, { month: "long" });
  return formatter.format(date);
};

// Funzione helper per il padding con zero
const pad = (num: number, size: number = 2): string => {
  return num.toString().padStart(size, "0");
};

// Formatter DateTime migliorato
export const dateTimeFormatter = (
  value: any,
  format?: string,
  locale: string = "it"
) => {
  // Gestisci valori null/undefined/empty
  if (value === null || value === undefined || value === "") {
    return "";
  }

  // Converti il valore in Date
  let dateValue: Date;

  if (value instanceof Date) {
    dateValue = value;
  } else if (typeof value === "string") {
    dateValue = new Date(value);
    if (isNaN(dateValue.getTime())) {
      return value; // Data non valida
    }
  } else if (typeof value === "number") {
    dateValue = new Date(value);
    if (isNaN(dateValue.getTime())) {
      return value;
    }
  } else {
    return value;
  }

  // Parsa il formato
  const formatParams = parseDateTimeFormat(format || "");

  // Se non c'è un formato valido, usa il comportamento di default
  if (!formatParams) {
    const fullLocale = getFullLocale(locale);
    const formatter = new Intl.DateTimeFormat(fullLocale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return formatter.format(dateValue);
  }

  const { pattern } = formatParams;

  // Estrai i componenti della data
  const day = dateValue.getDate();
  const month = dateValue.getMonth() + 1; // 0-indexed
  const year = dateValue.getFullYear();
  const hours = dateValue.getHours();
  const minutes = dateValue.getMinutes();
  const seconds = dateValue.getSeconds();

  // Sostituisci i placeholder nel pattern
  let result = pattern;

  // IMPORTANTE: Sostituisci %mm (nome mese) PRIMA di %m (numero mese)
  // altrimenti %mm verrebbe interpretato come %m + m
  if (result.includes("%mm")) {
    const monthName = getMonthName(dateValue, locale);
    result = result.replace("%mm", monthName);
  }

  // Sostituisci i placeholder rimanenti
  result = result.replace("%d", pad(day));
  result = result.replace("%m", pad(month));
  result = result.replace("%Y", year.toString());
  result = result.replace("%H", pad(hours));
  result = result.replace("%M", pad(minutes));
  result = result.replace("%S", pad(seconds));

  return result;
};

// Funzione per parsare il formato decimale
export const parseDecimalFormat = (
  format: string
): DecimalFormatParams | null => {
  if (!format || format === "") {
    return null; // Mantieni così com'è
  }

  // Pattern: "dec:X|lang=YY" o "dec:X|lang=YY,tsep=1"
  const match = format.match(/^dec:(\*|\d+)\|lang=([a-z]{2})(,tsep=1)?$/i);

  if (!match) {
    return null;
  }

  const [, decimalsStr, locale, tsep] = match;

  return {
    decimals: decimalsStr === "*" ? "*" : parseInt(decimalsStr, 10),
    locale: locale,
    useThousandsSeparator: !!tsep,
  };
};

// Formatter decimale migliorato che gestisce stringhe
export const decimalFormatter = (
  value: any,
  format?: string,
  defaultLocale: string = "it"
) => {
  // Se il valore è null, undefined o stringa vuota, restituiscilo così com'è
  if (value === null || value === undefined || value === "") {
    return value;
  }

  // Converti il valore in numero se è una stringa
  let numValue: number;
  if (typeof value === "string") {
    numValue = parseFloat(value);
    // Se la conversione fallisce (NaN), restituisci il valore originale
    if (isNaN(numValue)) {
      return value;
    }
  } else if (typeof value === "number") {
    numValue = value;
  } else {
    // Se non è né string né number, restituisci così com'è
    return value;
  }

  // Parsa il formato
  const formatParams = parseDecimalFormat(format || "");

  // Se non c'è un formato valido, usa il comportamento di default
  if (!formatParams) {
    const formatter = new Intl.NumberFormat(defaultLocale, {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
    return formatter.format(numValue);
  }

  const { decimals, locale, useThousandsSeparator } = formatParams;

  // Costruisci le opzioni per Intl.NumberFormat
  const options: Intl.NumberFormatOptions = {
    useGrouping: useThousandsSeparator,
  };

  if (decimals === "*") {
    // Tutti i decimali significativi (rimuove trailing zeros)
    options.minimumFractionDigits = 0;
    options.maximumFractionDigits = 20; // Limite massimo ragionevole
  } else {
    // Numero specifico di decimali
    options.minimumFractionDigits = decimals;
    options.maximumFractionDigits = decimals;
  }

  const formatter = new Intl.NumberFormat(locale, options);
  return formatter.format(numValue);
};

// Funzione per parsare il formato booleano
export const parseBooleanFormat = (
  format: string
): BooleanFormatParams | null => {
  if (!format || format === "") {
    return null;
  }

  // Pattern: "bool:%x" o "bool:%T" o "bool:%Y" o "bool:%1"
  const match = format.match(/^bool:%([xTY1])$/);

  if (!match) {
    return null;
  }

  const [, type] = match;

  return {
    type: type as BooleanFormatType,
  };
};

// Formatter booleano
export const booleanFormatter = (
  value: any,
  format?: string,
  locale: string = "it"
) => {
  // Gestisci valori null/undefined/empty
  if (value === null || value === undefined || value === "") {
    return "";
  }

  // Converti il valore in booleano
  let boolValue: boolean;

  if (typeof value === "boolean") {
    boolValue = value;
  } else if (typeof value === "string") {
    const lowerValue = value.toLowerCase().trim();
    // Gestisci vari formati di stringhe booleane
    boolValue =
      lowerValue === "true" ||
      lowerValue === "1" ||
      lowerValue === "yes" ||
      lowerValue === "si";
  } else if (typeof value === "number") {
    boolValue = value !== 0;
  } else {
    return value; // Restituisci il valore originale se non è convertibile
  }

  // Parsa il formato
  const formatParams = parseBooleanFormat(format || "");

  // Se non c'è un formato valido, usa il comportamento di default (checkbox icon o true/false)
  if (!formatParams) {
    return boolValue ? "true" : "false";
  }

  const { type } = formatParams;

  // Applica la formattazione in base al tipo
  switch (type) {
    case "x":
      return boolValue ? "x" : " "; // x o spazio

    case "T":
      // Vero / Falso (con supporto i18n)
      if (locale.startsWith("it")) {
        return boolValue ? "Vero" : "Falso";
      } else if (locale.startsWith("en")) {
        return boolValue ? "True" : "False";
      } else if (locale.startsWith("es")) {
        return boolValue ? "Verdadero" : "Falso";
      } else if (locale.startsWith("fr")) {
        return boolValue ? "Vrai" : "Faux";
      } else if (locale.startsWith("de")) {
        return boolValue ? "Wahr" : "Falsch";
      }
      return boolValue ? "True" : "False";

    case "Y":
      // Si / No (con supporto i18n)
      if (locale.startsWith("it")) {
        return boolValue ? "Si" : "No";
      } else if (locale.startsWith("en")) {
        return boolValue ? "Yes" : "No";
      } else if (locale.startsWith("es")) {
        return boolValue ? "Sí" : "No";
      } else if (locale.startsWith("fr")) {
        return boolValue ? "Oui" : "Non";
      } else if (locale.startsWith("de")) {
        return boolValue ? "Ja" : "Nein";
      }
      return boolValue ? "Yes" : "No";

    case "1":
      return boolValue ? "1" : "0";

    default:
      return boolValue ? "true" : "false";
  }
};

// Aggiorna retColumn per usare il formato
export const retColumn = (
  col: ColumnDescriptor,
  statusName?: string,
  option?: IStandardColumnOption
) => {
  const gridCol: GridColDef = {
    field: col.ColumnId,
    headerName: col.LanguageLabel,
    width: 150,
    filterable: true,
    hideable: true,
    pinnable: true,
    resizable: true,
    sortable: col.Sortable,
    align: col.ColumnId === "StatusColor" ? "center" : undefined,
    visible: col.Show ?? true,
    valueFormatter: (value) => {
      // Colonne ID con tipo intero (comportamento speciale per ID)
      if (
        col.ColumnId.toLowerCase().startsWith("id") &&
        col.ColType === ColumnType.IntegerType
      ) {
        return numberFormatter(value);
      }

      // Colonne Integer con formato custom
      if (col.ColType === ColumnType.IntegerType && col.Format) {
        return integerFormatter(value, col.Format, currentLocale);
      }

      // Colonne Integer senza formato (default)
      if (col.ColType === ColumnType.IntegerType) {
        return integerFormatter(value, "int:", currentLocale);
      }

      // Colonne data
      if (col.ColType === ColumnType.DateTimeType) {
        return dateTimeFormatter(value, col.Format, currentLocale);
      }

      // Colonne decimali
      if (col.ColType === ColumnType.DecimalType) {
        return decimalFormatter(value, col.Format);
      }
      // Colonne booleane
      if (col.ColType === ColumnType.BooleanType) {
        return booleanFormatter(value, col.Format, currentLocale);
      }

      return value;
    },

    type: convertColumnType(col.ColType),

    // Gestione valori nulli per colonne di tipo date
    valueGetter:
      col.ColType === ColumnType.DateTimeType
        ? (value, row) => {
            return value ? new Date(value) : null;
          }
        : undefined,

    ...(col.ColumnId === "StatusColor"
      ? {
          renderCell: (params) => {
            const value = params.value || "#ccc, ";
            const [color, ...textParts] = value.split(",");
            return (
              <div
                title={textParts.join(",").trim()}
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  border: "4px solid gray",
                  backgroundColor: `${color}`,
                  display: "inline-block",
                }}
              ></div>
            );
          },
        }
      : {}),

    // gestione boolean con renderCell quando ha un formato custom
    ...(col.ColType === ColumnType.BooleanType && col.Format
      ? {
          renderCell: (params) => {
            const formattedValue = booleanFormatter(
              params.value,
              col.Format,
              currentLocale
            );
            return <span>{formattedValue}</span>;
          },
        }
      : {}),

    ...(col.ColType === ColumnType.ShortTextType ||
    col.ColType === ColumnType.LongTextType
      ? {
          getApplyQuickFilterFn: getApplyQuickFilterFnString,
        }
      : {}),

    ...option,
  };
  return gridCol;
};

const getApplyQuickFilterFnString: GetApplyQuickFilterFn<any, unknown> = (
  value: any
) => {
  // if (!value || value.length !== 4 || !/\d{4}/.test(value)) {
  //   // If the value is not a 4-digit string, it cannot be a year so applying this filter is useless
  //   return null;
  // }
  return (cellValue: any) => {
    if (cellValue) {
      return cellValue.toString().toLowerCase().includes(value.toLowerCase());
    }
    return false;
  };
};

// export const getColumnVisibility = (
//   columnName: keyof typeof StandardColumns
// ): boolean | undefined => {
//   // Recupera la funzione associata alla colonna
//   const columnFunction = StandardColumns[columnName];
//   if (!columnFunction) {
//     console.warn(`Column "${columnName}" not found.`);
//     return undefined; // Restituisci undefined se il nome della colonna non esiste
//   }

//   // Crea l'oggetto colonna con l'opzione fornita
//   const column = columnFunction() as IStandardColumnOption;

//   // Restituisci il valore di `visible`
//   return column.visible;
// };
export const getColumnVisibility = (
  fieldName: string, // Nome del campo in formato stringa
  option?: IStandardColumnOption
): boolean | undefined => {
  // Cerca dinamicamente il campo in StandardColumns
  const columnEntry = Object.entries(StandardColumns).find(
    ([key]) => key === fieldName
  );

  if (!columnEntry) {
    console.warn(`Column "${fieldName}" not found.`);
    return undefined; // Campo non trovato
  }

  // Recupera la funzione del campo e crea la colonna
  const [, columnFunction] = columnEntry;
  const column = columnFunction(option) as IStandardColumnOption;

  // Restituisci il valore di `visible`
  return column.visible;
};
